import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { map, take } from 'rxjs';

import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { SettingsQueries } from 'src/app/shared/types/get-query/settings.enum';
import { Paginable } from 'src/app/shared/types/paginable';
import { UserProfileRole, UserProfileRoleFilters } from '../../models/types/user-profile-role.type';
import { UserProfileRoleMapper } from '../../services/user-profile-role-mapper';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import {
	AddUserProfileRoleModalComponent,
	AddUserProfileRoleModalData,
	AddUserProfileRoleResult
} from './add-user-profile-role-modal/add-user-profile-role-modal.component';

@Component({
	standalone: false,
	selector: 'bt-company-role-settings',
	templateUrl: './company-role-settings.component.html',
	styleUrls: ['./company-role-settings.component.scss']
})
export class CompanyRoleSettingsComponent implements OnInit {
	readonly #graphQlService = inject(GraphQLService);
	readonly #destroyRef = inject(DestroyRef);
	readonly #utilitiesService = inject(UtilitiesService);
	readonly #route = inject(ActivatedRoute);
	readonly #router = inject(Router);
	readonly #dialog = inject(MatDialog);
	readonly #userProfileRoleMapper = inject(UserProfileRoleMapper);

	userProfileRoles: Paginable<UserProfileRole> = new Paginable<UserProfileRole>();
	userProfileRoleFilters = new UserProfileRoleFilters();
	loading = false;

	ngOnInit(): void {
		this.#route.queryParams.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((params: Params) => {
			this.userProfileRoleFilters = this.#userProfileRoleMapper.urlToFilters(params);

			this.userProfileRoles.currentPage = this.userProfileRoleFilters.page || 1;
			this.userProfileRoles.size = this.userProfileRoleFilters.size || 20;

			this.fetchUserProfileRoles();
		});
	}

	pageEvent(event: { pageNumber: number; pageSize: number }) {
		this.userProfileRoles.currentPage = event.pageNumber;
		this.userProfileRoles.size = event.pageSize;

		this.userProfileRoleFilters.page = event.pageNumber;
		this.userProfileRoleFilters.size = event.pageSize;

		this.refreshNavigation();
	}

	openAddUserProfileRoleModal(item?: UserProfileRole): void {
		const data: AddUserProfileRoleModalData | undefined = item
			? {
					roleId: item.role.id,
					userProfileId: item.userProfile.id,
					userProfileRoleId: item.id
				}
			: undefined;

		this.#dialog
			.open(AddUserProfileRoleModalComponent, {
				width: '800px',
				data
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe((result: AddUserProfileRoleResult | undefined) => {
				if (!result) {
					return;
				}

				if (result.userProfileRoleId) {
					this.updateUserProfileRole(result.userProfileRoleId, result.roleId);
				} else {
					this.addUserProfileRole(result.roleId, result.userProfileId);
				}
			});
	}

	deleteUserProfileRole(userProfileRoleId: number) {
		this.#dialog
			.open(ConfirmModalComponent, {
				data: {
					title: 'Remove User Profile Role',
					message: 'Are you sure you want to remove this user profile role?'
				}
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe((confirm: boolean) => {
				if (!confirm) {
					return;
				}

				this.loading = true;
				this.#graphQlService
					.postData(MutationQuery.DELETE_USER_PROFILE_ROLE, {
						deleteUserProfileRoleInput: {
							userProfileRoleId: userProfileRoleId
						}
					})
					.pipe(takeUntilDestroyed(this.#destroyRef))
					.subscribe({
						next: () => {
							this.fetchUserProfileRoles();
						},
						error: (err) => {
							this.loading = false;
							this.#utilitiesService.showSnackbar(
								err?.networkError?.error?.errors?.[0]?.message || 'Error deleting user profile role',
								SnackbarType.Error
							);
							console.error(err);
						}
					});
			});
	}

	private addUserProfileRole(roleId: number, userProfileId: number) {
		this.loading = true;
		this.#graphQlService
			.postData(MutationQuery.ADD_USER_PROFILE_ROLE, {
				addRoleToUserInput: {
					roleId: roleId,
					userProfileId: userProfileId
				}
			})
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: () => {
					this.fetchUserProfileRoles();
				},
				error: (err) => {
					this.loading = false;
					this.#utilitiesService.showSnackbar(
						err?.networkError?.error?.errors?.[0]?.message || 'Error adding user profile role',
						SnackbarType.Error
					);
					console.error(err);
				}
			});
	}

	private updateUserProfileRole(userProfileRoleId: number, roleId: number) {
		this.loading = true;
		this.#graphQlService
			.postData(MutationQuery.UPDATE_USER_PROFILE_ROLE, {
				updateRoleToUserInput: {
					userProfileRoleId: userProfileRoleId,
					roleId: roleId
				}
			})
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: () => {
					this.fetchUserProfileRoles();
				},
				error: (err) => {
					this.loading = false;
					this.#utilitiesService.showSnackbar(
						err?.networkError?.error?.errors?.[0]?.message || 'Error updating user profile role',
						SnackbarType.Error
					);
					console.error(err);
				}
			});
	}

	private fetchUserProfileRoles() {
		this.loading = true;

		const pagination = this.userProfileRoles.buildQuery(this.userProfileRoles.currentPage);

		this.#graphQlService
			.getData<any>(SettingsQueries.GET_USER_PROFILE_ROLES, {
				where: this.#userProfileRoleMapper.toWhereClause(this.userProfileRoleFilters),
				...pagination
			})
			.pipe(
				take(1),
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => {
					const userProfileRoles = res.data.userProfileRoles;

					return new Paginable<UserProfileRole>({
						items: userProfileRoles.nodes.map((node: any) => this.#userProfileRoleMapper.toUserProfileRole(node)),
						totalCount: userProfileRoles.totalCount,
						currentPage: this.userProfileRoles.currentPage,
						size: this.userProfileRoles.size
					});
				})
			)
			.subscribe({
				next: (data: Paginable<UserProfileRole>) => {
					this.userProfileRoles = data;
					this.loading = false;
				},
				error: (err) => {
					this.#utilitiesService.showSnackbar('Error fetching User Profile Roles', SnackbarType.Error);
					this.loading = false;
					console.error(err);
				}
			});
	}

	private refreshNavigation() {
		this.loading = true;

		this.#router.navigate([location.pathname], {
			queryParams: this.#userProfileRoleMapper.filtersToUrl(this.userProfileRoleFilters)
		});
	}
}
